import { Component, OnDestroy, OnInit } from '@angular/core';
import { AppScopeMapperGridColumnsModel } from './model/app-scope-mapper-grid-columns.model';
import { Store } from '@ngxs/store';
import { GlobalConfigurationState } from '../../state/global-configuration.state';
import { ICellRendererParams } from 'ag-grid-community';
import { AppScopeMapperService } from './service/app-scope-mapper.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { BroadcastService, EditableConfigGridData, EditableGridComponent, ToasterType } from '@cv-invision/core';
import { Router } from '@angular/router';

@Component({
	selector: 'cv-invision-app-scope-mapper',
	templateUrl: './app-scope-mapper.component.html',
	styleUrls: ['./app-scope-mapper.component.scss']
})
export class AppScopeMapperComponent extends EditableGridComponent implements OnInit, OnDestroy {
	private appScopeMapperService: AppScopeMapperService;
	private atComponentDestroy = new Subject();
	public saving: boolean;
	public isDirtyForm: boolean;
	private entity: string;

	constructor(private store: Store,
				private broadcastService: BroadcastService,
				appScopeMapperService: AppScopeMapperService,
				private router: Router,
	) {
		super(appScopeMapperService);
		this.appScopeMapperService = appScopeMapperService;
	}

	ngOnInit(): void {
		this.setConfiguration();
		this.appScopeMapperService.setConfiguration(this.entity);
		this.appScopeMapperService
			.getFormDataListener()
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((result: EditableConfigGridData) => {
				if (result.doSave !== undefined && result.doSave) {
					this.onSave();
				}
				if (result.isDirty !== undefined) {
					this.isDirtyForm = result.isDirty;
				}
				if (result.saving !== undefined) {
					this.saving = result.saving;
				}
			});

		let acuityTypes: any = [];
		let cmpAcuityTypes: any = [];

		switch (this.entity) {
			case 'property-types':
				acuityTypes = this.store.selectSnapshot(GlobalConfigurationState.getAcuityPropertyTypes);
				cmpAcuityTypes = this.store.selectSnapshot(GlobalConfigurationState.getCmpAcuityPropertyTypes);
				break;
			case 'loan-types':
				acuityTypes = this.store.selectSnapshot(GlobalConfigurationState.getAcuityLoanTypes);
				cmpAcuityTypes = this.store.selectSnapshot(GlobalConfigurationState.getCmpAcuityLoanTypes);
				break;
			case 'loan-reasons':
				acuityTypes = this.store.selectSnapshot(GlobalConfigurationState.getAcuityLoanReasons);
				cmpAcuityTypes = this.store.selectSnapshot(GlobalConfigurationState.getCmpAcuityLoanReasons);
				break;
			case 'occupancy-types':
				acuityTypes = this.store.selectSnapshot(GlobalConfigurationState.getAcuityOccupancyTypes);
				cmpAcuityTypes = this.store.selectSnapshot(GlobalConfigurationState.getCmpAcuityOccupancyTypes);
				break;
			case 'document-types':
				acuityTypes = this.store.selectSnapshot(GlobalConfigurationState.getAcuityDocumentTypes);
				cmpAcuityTypes = this.store.selectSnapshot(GlobalConfigurationState.getCmpAcuityDocumentTypes);
				break;
		}

		this.gridOptions.columnDefs = new AppScopeMapperGridColumnsModel()
			.getColumns(acuityTypes, cmpAcuityTypes);
		this.loadGridData();
	}

	/**
	 * Set configuration according to routes
	 */
	private setConfiguration()
	{
		const url = this.router.url;
		if (url.indexOf('property-types') !== -1) {
			this.entity = 'property-types';
		}

		if (url.indexOf('loan-types') !== -1) {
			this.entity = 'loan-types';
		}

		if (url.indexOf('loan-reasons') !== -1) {
			this.entity = 'loan-reasons';
		}

		if (url.indexOf('occupancy-types') !== -1) {
			this.entity = 'occupancy-types';
		}

		if (url.indexOf('document-types') !== -1) {
			this.entity = 'document-types';
		}
	}

	/**
	 * Save current configuration.
	 */
	onSave(): void {
		const currentConfig = this.config.map( item => ({scopeId: item.scopeId, acuityId: item.acuityId, cmpAcuityId: item.cmpAcuityId}));

		this.appScopeMapperService
			.saveAppScopeConfiguration(currentConfig)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				this.loadGridData();
				if (result && result.succeeded) {
					this.broadcastService.broadcast({
						name: ToasterType.SUCCESS,
						event: { message: 'Changes saved successfully' }
					});
				}
				this.appScopeMapperService.setFormData({saving: false, doSave: false});
			}, error => {
				console.error(error);
				this.broadcastService.broadcast({
					name: ToasterType.ERROR,
					event: { error: 'Error occurred ' + error.error.message }
				});
				this.appScopeMapperService.setFormData({saving: false, doSave: false});
			});
	}

	/**
	 * On cell updated change value of configuration and emit dirty change.
	 */
	onCellUpdated(params: ICellRendererParams, newData: any): void {
		const scopeId = params.data.scopeId;
		let match = this.config.find( item => item.scopeId === scopeId);
		if (match) {
			match = Object.assign(match, newData);
		}
		this.emitDirtyness();
	}

	/**
	 * Load grid data implementation to load the property types.
	 */
	loadGridData(): void {
		this.appScopeMapperService
			.getAppScopeConfiguration()
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				this.originalConfig = JSON.parse(JSON.stringify(result));
				this.config = result;
				this.gridData = this.config;
				this.gridPagination.pagination.limit = result.length;
				this.gridPagination.pagination.totalCount = result.length;
				this.gridHeight = this.getGridHeight(result && result.length > 0 ? result.length : 0);
			});
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
